/**
 * NotificationsPage.js
 * ====================
 *
 * Full-page notification feed reachable from the bell drawer's
 * "View all notifications" link. The bell menu shows the most
 * recent 50 entries; this page lets the user paginate through the
 * complete history, filter by read state, click into individual
 * notifications, and clear the unread badge in one shot.
 *
 * State management
 * ----------------
 * Uses a SEPARATE local state from the redux ``state.notifications``
 * slice so the user's pagination/filter choices on this page don't
 * fight with the bell's 20s polling timer (which replaces the redux
 * items list to stay current). Local state means: this page issues
 * its own ``GET /v1/notifications?page=X&per_page=50&unread=...``
 * calls and keeps the answer in component state. ``markRead`` /
 * ``markAllRead`` actions are still dispatched through redux so the
 * bell badge updates in real time.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Box, Button, Chip, CircularProgress, Divider,
  IconButton, MenuItem, Pagination, Paper, Select, Stack, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import {
  InfoOutlined as InfoOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
  Refresh as RefreshIcon,
  DoneAll as DoneAllIcon,
  Settings as SettingsIcon,
  NotificationsNone as NotificationsNoneIcon,
} from '@mui/icons-material';

import {
  markRead as markReadAction,
  markAllRead as markAllReadAction,
} from '../../redux/actions/notifications';
import {
  fetchRequest as pageListRequest,
  clearState as pageListClear,
} from '../../redux/actions/notifications/pageList';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { isExternalLink } from '../../utils/notificationLink';


const PER_PAGE = 50;

const SEVERITY_META = {
  info:     { Icon: InfoOutlinedIcon,        toneKey: 'info'    },
  success:  { Icon: CheckCircleOutlineIcon,  toneKey: 'success' },
  warning:  { Icon: WarningAmberOutlinedIcon, toneKey: 'warning' },
  critical: { Icon: ErrorOutlineIcon,        toneKey: 'error'   },
};

const _toneColor = (theme, toneKey) => {
  switch (toneKey) {
    case 'info':    return theme.palette.info.main;
    case 'success': return theme.palette.success.main;
    case 'warning': return theme.palette.warning.main;
    case 'error':   return theme.palette.error.main;
    default:        return theme.palette.text.secondary;
  }
};

const _relativeTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const dt = (Date.now() - t) / 1000;
  if (dt < 60) return 'just now';
  if (dt < 3600) return `${Math.floor(dt / 60)}m ago`;
  if (dt < 86400) return `${Math.floor(dt / 3600)}h ago`;
  if (dt < 86400 * 7) return `${Math.floor(dt / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};


const NotificationsPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Local state: pagination + filter + items + counts. We don't read
  // from redux for the list because the bell's 20s polling timer
  // replaces that slice with the latest 50 - which would override
  // the user's paged/filtered view here.
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');   // 'all' | 'unread'
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  // Dedicated notifications_page_list slice (separate from the bell's
  // polled `notifications` slice, see the header comment).
  const pageState = useSelector((s) => s.notifications_page_list);
  const loading = Boolean(pageState?.isLoading);
  const error = pageState?.error || null;

  const fetchPage = useCallback(() => {
    dispatch(pageListRequest({
      page,
      perPage: PER_PAGE,
      unread: filter === 'unread',
    }));
  }, [dispatch, page, filter]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  // A page landed — mirror it into local state (local `items` stays the
  // render source of truth so the optimistic mark-read updates below
  // keep working without re-fetching).
  useEffect(() => {
    const payload = pageState?.response;
    if (!payload) return;
    setItems(payload.items || []);
    setTotal(payload.pagination?.total ?? 0);
    setUnreadCount(payload.unread_count ?? 0);
  }, [pageState?.response]);

  // Start from a clean slate on the next visit (the page always
  // begins with its own fetch, like the previous local-state version).
  useEffect(() => () => { dispatch(pageListClear()); }, [dispatch]);

  // Reset to page 1 when the filter changes, then re-fetch.
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setPage(1);
  };

  const handleRowClick = (n) => {
    if (n.notification_id && !n.read_at) {
      // Optimistic local update so the row dims immediately, plus a
      // dispatched action so the bell unread-count drops too.
      setItems((prev) => prev.map((row) =>
        row.notification_id === n.notification_id
          ? { ...row, read_at: new Date().toISOString() }
          : row,
      ));
      setUnreadCount((u) => Math.max(0, u - 1));
      dispatch(markReadAction(n.notification_id));
    }
    if (n.link) {
      // Router-level resolvers (App.js) handle workspace-prefixed and
      // legacy link shapes; absolute URLs leave the SPA.
      if (isExternalLink(n.link)) window.open(n.link, '_blank', 'noopener');
      else navigate(n.link);
    }
  };

  const handleMarkAllRead = async () => {
    // Dispatch through redux so the bell stays in sync, then re-fetch.
    dispatch(markAllReadAction());
    // Optimistic local update for instant feedback.
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    setUnreadCount(0);
    // Re-fetch to converge with the BE's view (especially relevant
    // when filter === 'unread' - the page should empty out).
    setTimeout(fetchPage, 250);
  };

  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));

  // Group rows by day so a long history reads naturally. Without this
  // a 200-row history is one long undifferentiated wall.
  const grouped = useMemo(() => {
    const out = [];
    let last = null;
    items.forEach((n) => {
      const day = n.created_at
        ? new Date(n.created_at).toLocaleDateString(undefined, {
          weekday: 'long', month: 'short', day: 'numeric',
          year: 'numeric',
        })
        : 'Unknown';
      if (day !== last) {
        out.push({ kind: 'header', day });
        last = day;
      }
      out.push({ kind: 'row', n });
    });
    return out;
  }, [items]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <PageBreadcrumbs
        items={[
          { text: 'Notifications', icon: <NotificationsNoneIcon /> },
        ]}
        sx={{ mb: 1 }}
      />

      {/* Header strip */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            {unreadCount > 0
              ? `${unreadCount} unread · ${total} total`
              : `${total} total · all read`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Select
            size="small"
            value={filter}
            onChange={handleFilterChange}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="unread">Unread only</MenuItem>
          </Select>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchPage} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={unreadCount === 0 ? 'Nothing to mark' : 'Mark all as read'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllRead}
                disabled={unreadCount === 0}
              >
                Mark all read
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Notification settings">
            <IconButton size="small" onClick={() => navigate('/settings/notifications')}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Body */}
      <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: 'hidden' }}>
        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        )}
        {!error && loading && items.length === 0 && (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!error && !loading && items.length === 0 && (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography variant="body1" sx={{ mt: 1 }}>
              {filter === 'unread' ? "You're all caught up." : 'No notifications yet.'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filter === 'unread'
                ? "Switch to All to see your history."
                : "We'll let you know when something happens in your workspaces."}
            </Typography>
          </Box>
        )}

        {!error && items.length > 0 && (
          <Box>
            {grouped.map((entry, idx) => {
              if (entry.kind === 'header') {
                return (
                  <Box
                    key={`h-${entry.day}`}
                    sx={{
                      px: 2, py: 1,
                      bgcolor: alpha(theme.palette.divider, 0.05),
                      borderTop: idx === 0 ? 0 : `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="caption"
                                sx={{ fontWeight: 700, letterSpacing: 0.5,
                                      textTransform: 'uppercase',
                                      color: 'text.secondary' }}>
                      {entry.day}
                    </Typography>
                  </Box>
                );
              }
              const n = entry.n;
              const meta = SEVERITY_META[n.severity] || SEVERITY_META.info;
              const SevIcon = meta.Icon;
              const sevColor = _toneColor(theme, meta.toneKey);
              const unread = !n.read_at;
              return (
                <Box
                  key={n.notification_id}
                  onClick={() => handleRowClick(n)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    cursor: n.link || unread ? 'pointer' : 'default',
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderLeft: `3px solid ${unread ? sevColor : 'transparent'}`,
                    bgcolor: unread ? alpha(sevColor, 0.04) : 'transparent',
                    '&:hover': {
                      bgcolor: alpha(sevColor, 0.08),
                    },
                  }}
                >
                  <Box sx={{ pt: 0.25, color: sevColor, flexShrink: 0 }}>
                    <SevIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center"
                           sx={{ mb: 0.25 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: unread ? 700 : 500,
                          flex: 1, minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {n.title}
                      </Typography>
                      {n.category && (
                        <Chip
                          label={n.category}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ flexShrink: 0 }}>
                        {_relativeTime(n.created_at)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block' }}>
                      {n.body}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {pageCount > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            shape="rounded"
          />
        </Stack>
      )}

      {/* Subtle inline reload indicator when fetching while the page already
          has content (avoids a full-screen spinner takeover). */}
      {loading && items.length > 0 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">Refreshing</Typography>
          </Box>
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="caption" color="text.secondary">
          Notifications older than 90 days are deleted automatically for
          informational events. Warnings and critical alerts are kept until you
          mark them as read.
        </Typography>
      </Stack>
    </Box>
  );
};

export default NotificationsPage;
